// How a navigation touches the browser history (WO #2440, "Back lands on weird pages"). The rules:
//   - opening a record or a real page PUSHES an entry, so Back returns to where the person was;
//   - a redirect, a dead-route fallback and an old-address rewrite REPLACE, so Back never lands on a page that only bounces forward again;
//   - the same address is never pushed twice;
//   - a filter, lane, sort or column change is not a navigation at all: it should call replaceUrlQuery (below), never navigate().
// docs/browser-back-navigation.md has the full list of cases.
export const REPLACE = Object.freeze({ replace: true });

// 'none' (already there), 'push', or 'replace'
export function historyMode(currentHash, nextUrl, opts = {}) {
  if (currentHash === nextUrl) return 'none';
  return opts && opts.replace ? 'replace' : 'push';
}

// Apply it to a history object (window.history in the app, a fake in tests).
export function applyHistory(hist, currentHash, nextUrl, state, opts = {}) {
  const mode = historyMode(currentHash, nextUrl, opts);
  if (mode === 'push') hist.pushState(state, '', nextUrl);
  else if (mode === 'replace') hist.replaceState(state, '', nextUrl);
  return mode;
}

// Change only the query of the current page (filters, lanes, sorts, columns) without adding a history entry.
export function replaceUrlQuery(hist, hash, params) {
  const path = String(hash || '').split('?')[0];
  const qp = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') qp.set(k, String(v)); });
  const qs = qp.toString();
  hist.replaceState(hist.state, '', qs ? `${path}?${qs}` : path);
}

// A "Back to X" button: when X is the page we just came from, go BACK in history (so the browser Back button and this button agree and there is no ping-pong);
// otherwise open X as a normal navigation. `from` is stored in the history state by navigate() in App.js.
export function goBackTo(hist, onNavigate, key, params, win = typeof window !== 'undefined' ? window : null) {
  const st = hist && hist.state;
  if (st && st.from === key && hist.length > 1) {
    // Step back until X is the current entry (an open card or modal keeps its own entries on top, and one step would only close it), so browser Back does not
    // bounce into the card afterwards. Then put X's return params (the lead that was open, the filters) on that entry without adding another: the entry we land on
    // may be an older one whose address does not carry them.
    if (win) { stepBackTo(hist, onNavigate, key, params, win); return 'back'; }
    hist.back(); return 'back';
  }
  if (onNavigate) onNavigate(key, params);
  return 'navigate';
}

// The query of the current address as plain strings (the only kind of parameter an address can carry).
export function queryOf(win) {
  const hash = String((win && win.location && win.location.hash) || '');
  const out = {};
  new URLSearchParams(hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '').forEach((v, k) => { out[k] = v; });
  return out;
}

// #2771: a Back control on a record or work page (the header arrow, "Back to X") goes to where the person CAME FROM when they arrived by an in-app navigation, the
// same place the browser Back button goes, and the page behind it puts its place back (view memory). The fixed parent (the CRM hub) is only for a page opened cold.
// Returns 'back' when it went back, 'none' when there was no origin (the caller then opens the parent).
export function backToOrigin(hist, onNavigate, currentKey, win = typeof window !== 'undefined' ? window : null) {
  const st = hist && hist.state;
  const from = st && st.from;
  if (!from || from === currentKey || !(hist.length > 1)) return 'none';
  return goBackTo(hist, onNavigate, from, undefined, win);
}

function stepBackTo(hist, onNavigate, key, params, win) {
  let steps = 0;
  const pathOf = () => String((win.location && win.location.hash) || '').split('?')[0];
  const here = pathOf();
  const onPop = () => {
    const there = pathOf() !== here; // the card's own entries share its page; the first entry on another page is where we came from
    if (!there && ++steps < 5) { hist.back(); return; }
    win.removeEventListener('popstate', onPop);
    // params undefined: keep what the entry we landed on already carries in its own address (the company that was open, the lane), instead of wiping it
    if (onNavigate) onNavigate(key, params === undefined ? queryOf(win) : params, REPLACE);
  };
  win.addEventListener('popstate', onPop);
  hist.back();
}

// The params for "Back to X" on a record page. An object param does not survive the address bar (see App.js), and every Back/forward press re-reads the route params from
// the address, so returnParams is gone by the time the button is pressed after the card was closed with browser Back. The primitive returnTab / returnSelectId are in the
// address, so rebuild from them when returnParams is missing.
export function returnParamsOf(routeParams) {
  const p = routeParams || {};
  if (p.returnParams) return p.returnParams;
  return {
    ...(p.returnTab ? { tab: p.returnTab } : {}),
    ...(p.returnFilter ? { filter: p.returnFilter } : {}),
    ...(p.returnSelectId ? { selectId: p.returnSelectId } : {}),
    ...(!p.returnTab && !p.returnFilter && !p.returnSelectId ? { tab: 'ready' } : {}),
  };
}
